import { doc, setDoc, serverTimestamp, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { ADVANCED_ROLES_CONFIG } from '../config/advancedRolesConfig';

/**
 * Script pour créer automatiquement tous les rôles par défaut du nouveau système
 * Ce script peut être exécuté manuellement pour initialiser la collection advanced_roles
 */

class AutoRoleCreator {
  /**
   * Crée tous les rôles par défaut du nouveau système
   * @param {string} userId - ID de l'utilisateur qui effectue la création
   * @returns {Promise<Object>} Résultat de la création
   */
  static async createAllDefaultRoles(userId) {
    try {
      console.log('🚀 Début de la création des rôles avancés par défaut...');
      
      const results = {
        created: 0,
        skipped: 0,
        errors: [],
        roles: []
      };

      const defaultRoles = ADVANCED_ROLES_CONFIG.DEFAULT_ROLES;

      for (const [roleKey, roleConfig] of Object.entries(defaultRoles)) {
        // Utiliser l'ID du rôle (en minuscules) plutôt que la clé (en majuscules)
        const roleId = roleConfig.id;
        
        try {
          // Vérifier si le rôle existe déjà
          const existingRole = await getDoc(doc(db, 'advanced_roles', roleId));
          
          if (existingRole.exists()) {
            console.log(`✅ Rôle ${roleId} existe déjà`);
            results.skipped++;
            results.roles.push({ id: roleId, status: 'exists' });
            continue;
          }

          // Créer le rôle avec les métadonnées complètes
          const roleData = {
            id: roleId,
            name: roleConfig.name,
            description: roleConfig.description,
            level: roleConfig.level,
            isSystem: roleConfig.isSystem !== undefined ? roleConfig.isSystem : true,
            isActive: true,
            permissions: roleConfig.permissions,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            createdBy: userId,
            updatedBy: userId,
            version: 1
          };

          await setDoc(doc(db, 'advanced_roles', roleId), roleData);
          
          console.log(`✅ Rôle ${roleId} créé avec succès`);
          results.created++;
          results.roles.push({ id: roleId, status: 'created', name: roleConfig.name });
          
        } catch (error) {
          console.error(`❌ Erreur lors de la création du rôle ${roleId}:`, error);
          results.errors.push({ role: roleId, error: error.message });
          results.roles.push({ id: roleId, status: 'error', error: error.message });
        }
      }

      console.log(`📊 Résultat: ${results.created} créés, ${results.skipped} existants, ${results.errors.length} erreurs`);
      
      if (results.errors.length === 0) {
        console.log('🎉 Tous les rôles ont été créés avec succès!');
      } else {
        console.warn('⚠️ Création terminée avec des erreurs');
      }

      return results;

    } catch (error) {
      console.error('❌ Erreur critique lors de la création des rôles:', error);
      throw error;
    }
  }

  /**
   * Vérifie l'état des rôles avancés
   * @returns {Promise<Object>} État des rôles
   */
  static async checkAdvancedRolesStatus() {
    try {
      const defaultRoles = ADVANCED_ROLES_CONFIG.DEFAULT_ROLES;
      const status = {
        totalDefaultRoles: Object.keys(defaultRoles).length,
        existingRoles: 0,
        missingRoles: [],
        allRolesExist: false
      };

      for (const [roleKey, roleConfig] of Object.entries(defaultRoles)) {
        const roleId = roleConfig.id;
        const existingRole = await getDoc(doc(db, 'advanced_roles', roleId));
        if (existingRole.exists()) {
          status.existingRoles++;
        } else {
          status.missingRoles.push(roleId);
        }
      }

      status.allRolesExist = status.existingRoles === status.totalDefaultRoles;

      console.log('📋 État des rôles avancés:');
      console.log(`- Rôles existants: ${status.existingRoles}/${status.totalDefaultRoles}`);
      console.log(`- Rôles manquants: ${status.missingRoles.length > 0 ? status.missingRoles.join(', ') : 'Aucun'}`);
      console.log(`- Système complet: ${status.allRolesExist ? '✅' : '❌'}`);

      return status;

    } catch (error) {
      console.error('❌ Erreur lors de la vérification des rôles:', error);
      return {
        totalDefaultRoles: Object.keys(ADVANCED_ROLES_CONFIG.DEFAULT_ROLES).length,
        existingRoles: 0,
        missingRoles: Object.values(ADVANCED_ROLES_CONFIG.DEFAULT_ROLES).map(role => role.id),
        allRolesExist: false,
        error: error.message
      };
    }
  }

  /**
   * Migre un utilisateur spécifique vers le nouveau système
   * @param {string} userId - ID de l'utilisateur à migrer
   * @param {string} targetRoleId - ID du rôle cible (optionnel)
   * @returns {Promise<Object>} Résultat de la migration
   */
  static async migrateUserToAdvancedRole(userId, targetRoleId = null) {
    try {
      console.log(`🚀 Migration de l'utilisateur ${userId} vers le nouveau système...`);
      
      // Récupérer les données de l'utilisateur
      const userDoc = await getDoc(doc(db, 'employees', userId));
      if (!userDoc.exists()) {
        throw new Error(`Utilisateur ${userId} non trouvé`);
      }

      const userData = userDoc.data();
      const currentRoleId = userData.role;
      const targetRole = targetRoleId || currentRoleId;

      // Vérifier que le rôle cible existe dans le nouveau système
      const roleDoc = await getDoc(doc(db, 'advanced_roles', targetRole));
      if (!roleDoc.exists()) {
        throw new Error(`Rôle ${targetRole} non trouvé dans advanced_roles`);
      }

      // Mettre à jour le rôle de l'utilisateur
      await setDoc(doc(db, 'employees', userId), {
        role: targetRole,
        updatedAt: serverTimestamp(),
        updatedBy: 'auto_migration'
      }, { merge: true });

      console.log(`✅ Utilisateur ${userId} migré vers le rôle ${targetRole}`);
      
      return {
        success: true,
        userId,
        oldRole: currentRoleId,
        newRole: targetRole
      };

    } catch (error) {
      console.error('❌ Erreur lors de la migration utilisateur:', error);
      throw error;
    }
  }
}

// Export pour utilisation dans la console de développement
if (typeof window !== 'undefined') {
  window.AutoRoleCreator = AutoRoleCreator;
  console.log('🔧 AutoRoleCreator disponible dans la console:');
  console.log('- AutoRoleCreator.createAllDefaultRoles(userId)');
  console.log('- AutoRoleCreator.checkAdvancedRolesStatus()');
  console.log('- AutoRoleCreator.migrateUserToAdvancedRole(userId, targetRoleId)');
}

export default AutoRoleCreator;
